import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Wrench, X} from 'lucide-react';
import {SessionStorage} from '@chekin/core';
import {cn} from '@chekinapp/ui';
import {
  formatMaintenanceTimes,
  getMaintenanceWindow,
  type MaintenanceWindow,
} from './getMaintenanceWindow';

const DISMISSED_KEY = 'maintenance_banner_dismissed';
const HEIGHT_VARIABLE = '--maintenance-banner-height';
const REFRESH_INTERVAL_MS = 60 * 1000;

const getDismissId = ({start, phase}: MaintenanceWindow) =>
  `${start.toISOString()}:${phase}`;

type MaintenanceBannerProps = {
  className?: string;
};

export function MaintenanceBanner({className}: MaintenanceBannerProps) {
  const {t, i18n} = useTranslation();
  const [maintenance, setMaintenance] = useState(() => getMaintenanceWindow());
  const [dismissedId, setDismissedId] = useState(() =>
    SessionStorage.get<string>(DISMISSED_KEY),
  );
  const bannerRef = useRef<HTMLDivElement>(null);
  const isVisible = maintenance !== null && dismissedId !== getDismissId(maintenance);

  useEffect(() => {
    const interval = setInterval(() => {
      const next = getMaintenanceWindow();
      setMaintenance(prev => (prev?.phase === next?.phase ? prev : next));
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  useLayoutEffect(() => {
    const banner = bannerRef.current;
    if (!isVisible || !banner) return;

    const root = document.documentElement;
    const updateHeight = () =>
      root.style.setProperty(HEIGHT_VARIABLE, `${banner.offsetHeight}px`);
    updateHeight();

    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateHeight);
    observer?.observe(banner);

    return () => {
      observer?.disconnect();
      root.style.removeProperty(HEIGHT_VARIABLE);
    };
  }, [isVisible]);

  if (!maintenance || !isVisible) return null;

  const times = formatMaintenanceTimes(maintenance, i18n.language);

  const handleDismiss = () => {
    const id = getDismissId(maintenance);
    SessionStorage.set(DISMISSED_KEY, id);
    setDismissedId(id);
  };

  return (
    <div className="h-[var(--maintenance-banner-height,0px)] shrink-0">
      <div
        ref={bannerRef}
        role="status"
        data-testid="maintenance-banner"
        className={cn(
          'fixed inset-x-0 top-0 z-40 flex items-center gap-3 bg-amber-100 px-4 pt-[10px] pb-3 text-[13px] leading-snug text-amber-950',
          className,
        )}
      >
        <Wrench
          size={20}
          strokeWidth={2.2}
          aria-hidden
          className="shrink-0 text-amber-700"
        />
        <p className="min-w-0 flex-1">
          <strong className="font-semibold">
            {t(`maintenance_banner.${maintenance.phase}_title`, times)}
          </strong>{' '}
          {t(`maintenance_banner.${maintenance.phase}`, times)}
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('maintenance_banner.dismiss')}
          className="-m-1 shrink-0 rounded p-1 text-amber-800 hover:bg-amber-200"
        >
          <X size={16} aria-hidden />
        </button>
      </div>
    </div>
  );
}
